import { Card } from '@/components/ui/card'
import { calculateFee, formatFee } from '@/lib/services/feeCalculation'
import { Wallet, Info, CheckCircle2 } from 'lucide-react'

interface FeeDisplayWidgetProps {
  numberOfSports: number
}

export function FeeDisplayWidget({ numberOfSports }: FeeDisplayWidgetProps) {
  const fee = calculateFee(numberOfSports)

  if (numberOfSports === 0) return null

  return (
    <Card className="overflow-hidden border-primary/20">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/90 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Registration Fee</h3>
            <p className="text-white/70 text-sm">
              {numberOfSports} sport{numberOfSports !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Fee Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="text-sm">Base Fee (1st Sport)</span>
            </div>
            <span className="font-medium text-slate-900">₹1,200</span>
          </div>

          {numberOfSports > 1 && (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span className="text-sm">
                  Additional Sports ({numberOfSports - 1} × ₹500)
                </span>
              </div>
              <span className="font-medium text-slate-900">
                ₹{(numberOfSports - 1) * 500}
              </span>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="flex items-center justify-between pt-2">
          <span className="text-lg font-semibold text-slate-900">Total Amount</span>
          <span className="text-3xl font-bold text-primary">{formatFee(fee)}</span>
        </div>

        {/* Info Note */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            This fee covers registration for all selected sports. Payment must be completed before submission.
          </p>
        </div>
      </div>
    </Card>
  )
}
